import React, { memo, useMemo } from 'react';
import {
    Modal,
    Box,
    Button,
    Slider,
    TextField,
    FormControl,
    IconButton,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Default styles
const defaultModalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
    width: { xs: '90%', sm: '80%', md: '40%' },
};

const closeButtonStyle = {
    position: 'absolute',
    top: 10,
    right: 10,
};

// Reusable SliderModal Component
const SliderModal = ({
    open,
    onClose,
    title = 'Slider Modal',
    sliderConfig = { min: 0, max: 100, step: 1, initialValue: 0 },
    sliderValue,
    onSliderChange,
    action,
    onAction,
    note,
    onNoteChange,
    showNoteOnReject = true,
    modalStyle = defaultModalStyle,
}) => {

    const validatedSliderConfig = useMemo(() => {
        const step = parseFloat(sliderConfig.step) || 1;
        const min = parseFloat(sliderConfig.min) || 0;
        const max = parseFloat(sliderConfig.max) || min + 1;

        if (max <= min) {
            console.warn("Invalid slider configuration: max should be greater than min");
            return { min, max: min + 1, step }; // Safe fallback
        }

        return { min, max, step };
    }, [sliderConfig]);
    const parsedSliderValue = parseFloat(sliderValue) || validatedSliderConfig.min;


    return (
        <>

            <Box display="flex" justifyContent="center" height="80vh">
                <Modal open={open} onClose={onClose}>
                    <Box sx={modalStyle}>
                        <IconButton onClick={onClose} sx={closeButtonStyle}>
                            <CloseIcon />
                        </IconButton>

                        <Typography variant="h6" gutterBottom>
                            {title}
                        </Typography>
                        {action !== 'Rejected' && (
                            <>
                                <Box display="flex" alignItems="center" gap={3} mt={2}>
                                    <Typography>${sliderConfig.min}</Typography>
                                    <Slider
                                        value={parsedSliderValue}
                                        min={validatedSliderConfig.min}
                                        max={validatedSliderConfig.max}
                                        step={validatedSliderConfig.step}
                                        onChange={onSliderChange}
                                        sx={{ flexGrow: 1 }}
                                    />
                                    <Typography>${validatedSliderConfig.max}</Typography>
                                </Box>

                                <FormControl fullWidth margin="normal">
                                    <TextField
                                        label="Selected Value"
                                        value={`$${sliderValue}`}
                                        disabled
                                    />
                                </FormControl>
                            </>
                        )}


                        {action === 'Rejected' && showNoteOnReject && (
                            <FormControl fullWidth margin="normal">
                                <TextField
                                    label="Note"
                                    placeholder="Provide a reason for rejection"
                                    multiline
                                    rows={3}
                                    value={note}
                                    onChange={onNoteChange}
                                />
                            </FormControl>
                        )}

                        <Box display="flex" justifyContent="space-between" mt={3}>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={() => onAction('Rejected')}
                            >
                                {(action === 'Rejected' && showNoteOnReject) ? "Reject" : "Reject & Reason"}
                            </Button>
                            {(action !== 'Rejected') && <Button
                                variant="contained"
                                color="primary"
                                onClick={() => onAction('Approved')}
                            >
                                Approved
                            </Button>}
                        </Box>
                    </Box>
                </Modal>
            </Box>

        </>

    );
};

export default memo(SliderModal);
